import { QRFrame } from '@/types/qr';

/**
 * Composites a framed QR code onto an HTMLCanvasElement.
 */
export async function renderFramedCanvas(
  qrImageOrCanvas: CanvasImageSource,
  frame: QRFrame | undefined,
  exportSize: number
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');

  if (!frame || frame.type === 'none') {
    canvas.width = exportSize;
    canvas.height = exportSize;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(qrImageOrCanvas, 0, 0, exportSize, exportSize);
    }
    return canvas;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    canvas.width = exportSize;
    canvas.height = exportSize;
    return canvas;
  }

  const scale = exportSize / 320;
  const qrSize = exportSize;
  const padding = 24 * scale;

  let totalWidth = qrSize + padding * 2;
  let totalHeight = qrSize + padding * 2;
  let qrX = padding;
  let qrY = padding;

  if (frame.type === 'bottom-badge') {
    const badgeHeight = 56 * scale;
    const badgeGap = 16 * scale;
    totalHeight += badgeHeight + badgeGap;
    qrX = padding;
    qrY = padding;
  } else if (frame.type === 'top-badge') {
    const badgeHeight = 56 * scale;
    const badgeGap = 16 * scale;
    totalHeight += badgeHeight + badgeGap;
    qrX = padding;
    qrY = padding + badgeHeight + badgeGap;
  } else if (frame.type === 'card-polaroid') {
    const footerHeight = 80 * scale;
    totalHeight += footerHeight;
    qrX = padding;
    qrY = padding;
  } else if (frame.type === 'minimal-border') {
    totalWidth += 16 * scale;
    totalHeight += 16 * scale;
    qrX = padding + 8 * scale;
    qrY = padding + 8 * scale;
  }

  canvas.width = totalWidth;
  canvas.height = totalHeight;

  // Background frame fill
  const cardRadius = 24 * scale;
  ctx.fillStyle = frame.backgroundColor || '#ffffff';
  ctx.strokeStyle = frame.borderColor || 'rgba(0,0,0,0.08)';
  ctx.lineWidth = 2 * scale;

  // Draw rounded card rectangle
  ctx.beginPath();
  ctx.roundRect(0, 0, totalWidth, totalHeight, cardRadius);
  ctx.fill();
  ctx.stroke();

  // Draw QR code
  ctx.drawImage(qrImageOrCanvas, qrX, qrY, qrSize, qrSize);

  // Draw frame text and badges
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (frame.type === 'bottom-badge') {
    const badgeWidth = Math.min(totalWidth - 48 * scale, 240 * scale);
    const badgeHeight = 44 * scale;
    const badgeX = (totalWidth - badgeWidth) / 2;
    const badgeY = qrY + qrSize + 16 * scale;
    const badgeRadius = badgeHeight / 2;

    // Badge pill
    ctx.fillStyle = frame.textColor || '#0071e3';
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, badgeRadius);
    ctx.fill();

    // Badge text
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(18 * scale)}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(frame.text || 'SCAN ME', totalWidth / 2, badgeY + badgeHeight / 2);
  } else if (frame.type === 'top-badge') {
    const badgeWidth = Math.min(totalWidth - 48 * scale, 240 * scale);
    const badgeHeight = 44 * scale;
    const badgeX = (totalWidth - badgeWidth) / 2;
    const badgeY = padding;
    const badgeRadius = badgeHeight / 2;

    // Header badge pill
    ctx.fillStyle = frame.textColor || '#0071e3';
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, badgeRadius);
    ctx.fill();

    // Header badge text
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(18 * scale)}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(frame.text || 'SCAN ME', totalWidth / 2, badgeY + badgeHeight / 2);
  } else if (frame.type === 'card-polaroid') {
    // Title
    ctx.fillStyle = frame.textColor || '#1d1d1f';
    ctx.font = `bold ${Math.round(20 * scale)}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(frame.text || 'Scan with Camera', totalWidth / 2, qrY + qrSize + 32 * scale);

    // Subtitle
    if (frame.subtext) {
      ctx.fillStyle = '#86868b';
      ctx.font = `${Math.round(14 * scale)}px system-ui, -apple-system, sans-serif`;
      ctx.fillText(frame.subtext, totalWidth / 2, qrY + qrSize + 56 * scale);
    }
  }

  return canvas;
}
